import * as CountersActions from './counters.actions';

import {MobileCounterConsumptionModel, MobileCounterListModel, MobileCounterModel} from '../../_models/counters.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {createReducer, on} from '@ngrx/store';
import {DahLoadingType} from '@common/components-module/core-components.models';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import dayjs from 'dayjs';

export const countersFeatureKey = 'counters';

export interface CountersState {
    list: MobileCounterListModel;
    listLoading?: DahLoadingType;
    itemLoading?: string[];
    acceptRequestLoading?: string[];

    openedItem: {
        counter?: MobileCounterModel;
        loading?: DahLoadingType;
        consumption?: MobileCounterConsumptionModel;
        consumptionLoading?: DahLoadingType;
    }
}

export const countersInitialsState: CountersState = {
    list: {},
    openedItem: {},
};

export const countersReducer = createReducer(
    countersInitialsState,

    on(CountersActions.LoadList, (state, action) => ({
        ...state,
        listLoading: action.payload.loading,
    })),

    on(CountersActions.LoadListSuccess, (state, action) => ({
        ...state,
        list: action.payload.response,
        listLoading: undefined,
    })),

    on(CountersActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined,
    })),


    on(CountersActions.AddCounter, (state) => ({
        ...state,
        listLoading: 'entityUpdating' as DahLoadingType,
    })),

    on(CountersActions.AddCounterSuccess, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            counters: [...state.list.counters!, action.payload.counter],
        },
        listLoading: undefined
    })),

    on(CountersActions.AddCounterFailure, (state) => ({
        ...state,
        listLoading: undefined,
    })),


    on(CountersActions.EditCounter, (state) => ({
        ...state,
        listLoading: 'entityUpdating' as DahLoadingType,
    })),

    on(CountersActions.EditCounterSuccess, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            counters: state.list.counters!.map(item =>
                item.id === action.payload.counter.id ? {...action.payload.counter} : item
            )
        },
        openedItem: {
            ...state.openedItem,
            ...state.openedItem?.counter && {
                counter: {
                    ...action.payload.counter,
                    indication: state.openedItem.counter.indication,
                },
            },
        },
        listLoading: undefined
    })),

    on(CountersActions.EditCounterFailure, (state) => ({
        ...state,
        listLoading: undefined,
    })),


    on(CountersActions.DeleteCounter, (state, action) => ({
        ...state,
        itemLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.payload.counterId, true)
    })),

    on(CountersActions.DeleteCounterSuccess, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            counters: state.list.counters!.filter(counter => counter.id !== action.payload.counterId),
        },
        itemLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.payload.counterId, false)
    })),

    on(CountersActions.DeleteCounterFailure, (state, action) => ({
        ...state,
        itemLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.payload.counterId, false)
    })),


    on(CountersActions.SendIndication, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            loading: action.payload.loading,
        }
    })),

    on(CountersActions.SendIndicationSuccess, (state, action) => ({
        ...state,
        list: {
            ...state.list,
            counters: state.list.counters!.map(item =>
                item.id === action.payload.response.id ? updateCounterIndication(item, action.payload.response) : item
            )
        },
        openedItem: {
            ...state.openedItem,
            counter: action.payload.response,
            loading: undefined,
        }
    })),

    on(CountersActions.SendIndicationFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            loading: undefined,
        }
    })),


    on(CountersActions.LoadIndication, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            loading: action.payload.loading,
        }
    })),

    on(CountersActions.LoadIndicationSuccess, (state, action) => {
        return {
            ...state,
            openedItem: {
                ...state.openedItem,
                counter: action.payload.counter,
                loading: undefined,
            }
        };
    }),

    on(CountersActions.LoadIndicationFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            loading: undefined,
        }
    })),


    on(CountersActions.SetOpenedItem, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            counter: action.payload,
        }
    })),


    on(CountersActions.ClearOpenedItem, (state) => ({
        ...state,
        openedItem: {},
    })),


    on(CountersActions.AcceptRequest, (state, action) => ({
        ...state,
        acceptRequestLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.counterId, true),
    })),

    on(CountersActions.AcceptRequestSuccess, (state, action) => ({
        ...state,
        acceptRequestLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.payload.counter.id!, false),
        list: {
            ...state.list,
            counters: state.list.counters!.map(item =>
                item.id === action.payload.counter.id ? {...action.payload.counter} : item
            )
        },
        openedItem: {
            ...state.openedItem,
            ...state.openedItem?.counter && {
                counter: {
                    ...action.payload.counter,
                    indication: state.openedItem.counter?.indication,
                }
            },
        }
    })),

    on(CountersActions.AcceptRequestFailure, (state, action) => ({
        ...state,
        acceptRequestLoading: StoredComponentHelperService.updateItemLoading(state.itemLoading, action.counterId, false),
    })),


    on(CountersActions.LoadConsumption, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            consumptionLoading: action.payload.silent ? undefined : 'listLoading' as DahLoadingType,
        }
    })),

    on(CountersActions.LoadConsumptionSuccess, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            consumption: action.payload.consumption,
            consumptionLoading: undefined,
        }
    })),

    on(CountersActions.LoadConsumptionFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            consumptionLoading: undefined,
        }
    })),


    on(CountersActions.IndicationChangedWsSuccess, (state, action) => {
        return {
            ...state,
            list: {
                ...state.list,
                counters: state.list.counters!.map(item =>
                    item.id === action.payload.counter.id ? updateCounterIndication(item, action.payload.counter) : item
                )
            }
        };
    }),


    on(SetAllInitialStates, () => ({...countersInitialsState})),
);

const updateCounterIndication = (item: MobileCounterModel, response: MobileCounterModel) => {
    const isOlder = dayjs(response.indication!.period).isBefore(dayjs(item.indication!.period));

    return {
        ...item,

        // Якщо поданий показник в цьому ж періоді або свіжіший - то просто затираємо
        ...!isOlder && {indication: response.indication},

        // Якщо поданий показник до поточної дати - оновити ті значення, що менші за актуальні
        ...isOlder && {
            indication: {
                ...item.indication,
                indication: {
                    1: {...item.indication!.indication![1], ...item.indication?.indication?.[1] && response.indication!.indication![1] && item.indication!.indication![1].end! < response.indication!.indication![1].end! && {end: response.indication!.indication![1].end}},
                    ...item.indication?.indication?.[2] && {
                        2: {...item.indication!.indication![2], ...item.indication?.indication?.[2] && response.indication!.indication![2] && item.indication!.indication![2].end! < response.indication!.indication![2].end! && {end: response.indication!.indication![2].end}}
                    },
                    ...item.indication?.indication?.[3] && {
                        3: {...item.indication!.indication![3], ...item.indication?.indication?.[3] && response.indication!.indication![3] && item.indication!.indication![3].end! < response.indication!.indication![3].end! && {end: response.indication!.indication![3].end}}
                    },
                }
            }
        }
    };
};
